import { useEffect, useMemo } from 'react';
import {
  Processor,
  StripePayoutSummaryItem,
  StripeTransactionItem,
  StripeTransactionsSummary,
} from '@weave/schema-gen-ts/dist/schemas/payments-platform/payouts/v1';
import { PayoutsAPI, serviceName } from '@frontend/api-payments';
import { useSchemaInfiniteQuery } from '@frontend/react-query-helpers';
import {
  calculateTotalGrossFromBuckets,
  computeStripePayoutSummaryFromTransactions,
} from '../utils/stripe-payout-transactions';

type StripePayoutTransactionsPageParam = string | undefined;

/**
 * Fetches Stripe payout transactions, bridging a backend migration from single-response to paginated.
 *
 * ## Background
 * The `ListLocationPayoutTransactions` endpoint is being rolled out in two modes:
 *
 * **Legacy (non-paginated):** Returns all transactions in one response with a server-computed
 * `payoutSummary`. No `nextPageToken` is present.
 *
 * **Paginated (new):** Returns transactions in pages via `nextPageToken`. The `payoutSummary`
 * field is null - the backend no longer computes it because it doesn't have all transactions
 * in scope for a single page.
 *
 * During the rollout, different locations may be on different backend versions, so this hook
 * detects which mode the backend is in at runtime and handles both.
 *
 * ## Strategy
 * 1. Always requests the paginated transaction list.
 * 2. Auto-fetches all pages via `nextPageToken`.
 * 3. Computes the summary on the client from the full transaction list.
 *
 */
export const useStripePayoutTransactions = ({ payoutId, locationId }: { payoutId: string; locationId: string }) => {
  const query = useSchemaInfiniteQuery({
    service: PayoutsAPI,
    serviceName: `${serviceName}-stripe`,
    endpointName: 'ListLocationPayoutTransactions',
    request: ({ pageParam }: { pageParam?: StripePayoutTransactionsPageParam }) => ({
      locationId,
      payoutId,
      processor: Processor.STRIPE,
      nextPageToken: pageParam || undefined,
    }),
    options: {
      refetchOnWindowFocus: false,
      enabled: !!payoutId,
      getNextPageParam: (lastPage): StripePayoutTransactionsPageParam => {
        const stripeData = 'stripeTransactionsSummary' in lastPage ? lastPage.stripeTransactionsSummary : undefined;
        return stripeData?.nextPageToken || undefined;
      },
    },
  });

  // Auto-fetch all remaining pages in paginated mode
  useEffect(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage();
    }
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const isLoading = query.isLoading || (!query.isError && (query.isFetchingNextPage || Boolean(query.hasNextPage)));

  const data = useMemo((): StripeTransactionsSummary | undefined => {
    if (!query.data?.pages) return undefined;

    const allTransactions: StripeTransactionItem[] = [];
    query.data.pages.forEach((page) => {
      if ('stripeTransactionsSummary' in page && page.stripeTransactionsSummary?.transaction?.transactionItems) {
        allTransactions.push(...page.stripeTransactionsSummary.transaction.transactionItems);
      }
    });

    const summaryBuckets = computeStripePayoutSummaryFromTransactions(allTransactions);

    return {
      payoutSummary: {
        payoutSummaryItems: summaryBuckets.map(
          ({ type, count, gross, fees, net }): StripePayoutSummaryItem => ({ type, count, gross, fees, net })
        ),
        totalGross: calculateTotalGrossFromBuckets(summaryBuckets),
      },
      transaction: {
        transactionItems: allTransactions,
      },
    } satisfies StripeTransactionsSummary;
  }, [query.data?.pages]);

  return {
    ...query,
    data,
    isLoading,
  };
};
